import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import config from "./config";

const APIUrl = config.API_BASE_URL + "/forgot-password";

// ─── Design tokens (mirrors Login/Register) ───────────────────────────────────
const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    cyan:   "#00c8ff",
    green:  "#00e896",
    red:    "#ff4466",
    amber:  "#ffb340",
};
const FONT = "'DM Mono', monospace";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
  * { box-sizing: border-box; }
  .fp-input {
    width: 100%;
    background: ${C.bg};
    border: 1px solid ${C.border};
    color: ${C.text};
    font-family: ${FONT};
    font-size: 12px;
    padding: 10px 14px;
    border-radius: 4px;
    outline: none;
    transition: border-color .15s;
    letter-spacing: .3px;
  }
  .fp-input:focus { border-color: ${C.cyan}; }
  .fp-input::placeholder { color: ${C.meta}; }
  .fp-input:disabled { opacity: 0.5; cursor: not-allowed; }
  .fp-btn {
    width: 100%;
    padding: 11px;
    background: ${C.cyan}15;
    border: 1px solid ${C.cyan}40;
    color: ${C.cyan};
    font-family: ${FONT};
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 1.2px;
    border-radius: 4px;
    cursor: pointer;
    transition: background .15s, border-color .15s;
  }
  .fp-btn:hover:not(:disabled) {
    background: ${C.cyan}25;
    border-color: ${C.cyan}70;
  }
  .fp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  @keyframes orbPulse {
    0%,100% { transform: scale(1); opacity: .15; }
    50%      { transform: scale(3); opacity: 0; }
  }
`;

const Orb = ({ color, size = 8, speed = "3s" }) => (
<div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
<div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: color, animation: `orbPulse ${speed} ease-in-out infinite` }} />
<div style={{ position: "absolute", inset: "25%", borderRadius: "50%", background: color }} />
</div>
);

const ForgotPassword = () => {
    const [email,          setEmail]          = useState("");
    const [loading,        setLoading]        = useState(false);
    const [submitted,      setSubmitted]      = useState(false);
    const [errorMessage,   setErrorMessage]   = useState(null);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!email.trim()) {
            setErrorMessage("Email address is required");
            return;
        }
        // Basic email format check
        if (!/\S+@\S+\.\S+/.test(email.trim())) {
            setErrorMessage("Please enter a valid email address");
            return;
        }
        setErrorMessage(null);
        setLoading(true);
        axios.post(APIUrl,
            { email: email.trim() },
            { headers: { "x-api-key": config.API_KEY } }
            )
            .then(() => {
                setLoading(false);
                setSubmitted(true);
            })
            .catch(() => {
                // Always show success regardless of outcome — security best practice
                // Do not reveal whether the email exists in the system
                setLoading(false);
                setSubmitted(true);
            });
    };

    return (
        <div style={{
        background:     C.bg,
            minHeight:      "100vh",
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            padding:        "20px",
            fontFamily:     FONT,
    }}>
    <style>{CSS}</style>

    <div style={{ width: "100%", maxWidth: 360 }}>

    {/* ── Brand mark ─────────────────────────────────────────────────── */}
    <div style={{ textAlign: "center", marginBottom: 32 }}>
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
    <Orb color={C.amber} size={10} speed="2.5s" />
        </div>
        <div style={{ fontFamily: FONT, fontWeight: 500, fontSize: 14, color: C.text, letterSpacing: "2px", marginBottom: 6 }}>
    BERBUL.AI
    </div>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1px" }}>
    AUTONOMOUS TRADING · SPY 0DTE
    </div>
    </div>

    {/* ── Card ───────────────────────────────────────────────────────── */}
    <div style={{
        background:   C.bg1,
            border:       `1px solid ${C.border}`,
            borderRadius: 12,
            padding:      "32px 32px 28px",
    }}>

    {submitted ? (
        /* ── Success state ─────────────────────────────────────────── */
    <div style={{ textAlign: "center" }}>
        {/* Amber check circle */}
    <div style={{
        width:        48,
            height:       48,
            borderRadius: "50%",
            background:   C.amber + "18",
            border:       `1px solid ${C.amber}40`,
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            margin:       "0 auto 20px",
    }}>
    <span style={{ fontSize: 20 }}>✉</span>
    </div>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 10 }}>
        Check your inbox
    </div>
    <div style={{ fontFamily: FONT, fontSize: 11, color: C.body, fontWeight: 300, lineHeight: 1.7, marginBottom: 24 }}>
        If that email is registered, you'll receive a password reset link shortly. Check your spam folder if you don't see it.
    </div>
    <Link to="/Login" style={{
        display:       "block",
            fontFamily:    FONT,
            fontSize:      10,
            color:         C.cyan,
            textDecoration:"none",
            fontWeight:    500,
            letterSpacing: ".5px",
    }}>
    ← Back to sign in
    </Link>
    </div>
    ) : (
        /* ── Request form ──────────────────────────────────────────── */
    <>
    {/* Card header */}
    <div style={{ marginBottom: 24 }}>
    <div style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: 6 }}>
        Reset password
    </div>
    <div style={{ fontFamily: FONT, fontSize: 11, color: C.body, fontWeight: 300 }}>
        Enter your email and we'll send a reset link
    </div>
    </div>

    {/* Email */}
    <div style={{ marginBottom: 24 }}>
    <label style={{ display: "block", fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: "1px", textTransform: "uppercase", marginBottom: 6 }}>
        Email address
    </label>
    <input
        className="fp-input"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        disabled={loading}
        placeholder="you@example.com"
        autoComplete="email"
        onKeyDown={e => e.key === "Enter" && handleSubmit(e)}
    />
    </div>

    {/* Error */}
        {errorMessage && (
        <div style={{
            background:   C.red + "12",
                border:       `1px solid ${C.red}30`,
                borderRadius: 4,
                padding:      "9px 12px",
                marginBottom: 16,
                display:      "flex",
                alignItems:   "center",
                gap:          8,
        }}>
        <div style={{ width: 5, height: 5, borderRadius: "50%", background: C.red, flexShrink: 0 }} />
        <span style={{ fontFamily: FONT, fontSize: 10, color: C.red }}>{errorMessage}</span>
        </div>
        )}

        {/* Submit */}
    <button className="fp-btn" onClick={handleSubmit} disabled={loading}>
        {loading ? "SENDING..." : "SEND RESET LINK"}
        </button>

        {/* Back to login */}
        <div style={{ marginTop: 20, textAlign: "center" }}>
    <Link to="/Login" style={{ fontFamily: FONT, fontSize: 10, color: C.meta, textDecoration: "none" }}>
    ← Back to sign in
    </Link>
    </div>
    </>
    )}
    </div>
    </div>
    </div>
    );
};

export default ForgotPassword;